import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface SearchScreenProps {
  onClose: () => void;
}

interface SearchResult {
  id: string;
  type: 'user' | 'channel';
  name: string;
  description?: string | null;
  avatar_url?: string | null;
  member_count?: number;
}

type FilterType = 'all' | 'users' | 'channels';

function SearchScreen({ onClose }: SearchScreenProps) {
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [recentSearches, setRecentSearches] = useState<SearchResult[]>([]);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length >= 2) {
      const delayDebounce = setTimeout(() => {
        searchAll();
      }, 300);

      return () => clearTimeout(delayDebounce);
    } else {
      setResults([]);
    }
  }, [query, activeFilter]); // Re-run when filter changes

  const searchAll = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const searchTerm = query.toLowerCase().trim();
      let allResults: SearchResult[] = [];

      // Only fetch what we need based on the active filter
      if (activeFilter === 'all' || activeFilter === 'users') {
        const { data: users } = await supabase
          .from('users')
          .select('id, username, display_name, avatar_url')
          .or(`username.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%`)
          .limit(5);

        if (users) {
          allResults.push(...users.map(user => ({
            id: user.id,
            type: 'user' as const,
            name: user.display_name || user.username,
            description: `@${user.username}`,
            avatar_url: user.avatar_url
          })));
        }
      }

      if (activeFilter === 'all' || activeFilter === 'channels') {
        const { data: channels } = await supabase
          .from('channels')
          .select('id, name, description, member_count')
          .ilike('name', `%${searchTerm}%`)
          .limit(5);

        if (channels) {
          allResults.push(...channels.map(channel => ({
            id: channel.id,
            type: 'channel' as const,
            name: channel.name,
            description: channel.description,
            member_count: channel.member_count
          })));
        }
      }

      setResults(allResults);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    // Trigger a new search when filter changes
    if (query.trim().length >= 2) {
      searchAll();
    }
  };

  return (
    <div className="fixed inset-0 bg-white z-50">
      <div className="flex items-center gap-2 p-2 border-b">
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600" />
        </button>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search SoundLink"
          className="flex-1 px-4 py-2 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
          autoFocus
        />
      </div>

      <div className="px-4 py-2 border-b">
        <div className="flex space-x-2">
          <button
            onClick={() => handleFilterChange('all')}
            className={`px-4 py-2 rounded-full transition-colors ${
              activeFilter === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleFilterChange('users')}
            className={`px-4 py-2 rounded-full transition-colors ${
              activeFilter === 'users'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => handleFilterChange('channels')}
            className={`px-4 py-2 rounded-full transition-colors ${
              activeFilter === 'channels'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Channels
          </button>
        </div>
      </div>

      <div className="overflow-auto h-[calc(100vh-128px)]">
        {query.length < 2 ? (
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-4">Recent</h2>
            {recentSearches.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No recent searches</p>
            ) : (
              <div className="space-y-2">
                {recentSearches.map(result => (
                  <SearchResultItem key={result.id} result={result} onClose={onClose} />
                ))}
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : results.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No {activeFilter === 'all' ? 'results' : activeFilter} found for "{query}"
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {results.map(result => (
              <SearchResultItem key={result.id} result={result} onClose={onClose} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface SearchResultItemProps {
  result: SearchResult;
  onClose: () => void;
}

function SearchResultItem({ result, onClose }: SearchResultItemProps) {
  if (result.type === 'user') {
    return (
      <Link
        to={`/profile/${result.id}`}
        onClick={onClose}
        className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
          {result.avatar_url ? (
            <img
              src={result.avatar_url}
              alt={result.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User className="h-6 w-6 text-gray-400" />
            </div>
          )}
        </div>
        <div>
          <div className="font-medium">{result.name}</div>
          <div className="text-sm text-gray-500">{result.description}</div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/channel/${result.name}`}
      onClick={onClose}
      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
    >
      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-xl font-bold text-gray-500">
        #
      </div>
      <div>
        <div className="font-medium">{result.name}</div>
        {result.description && (
          <div className="text-sm text-gray-500 line-clamp-1">{result.description}</div>
        )}
        <div className="text-sm text-gray-500">
          {result.member_count} {result.member_count === 1 ? 'member' : 'members'}
        </div>
      </div>
    </Link>
  );
}

export default SearchScreen;